import {
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  IconButton,
  useTheme,
} from "@mui/material";
import RecurringPledges from "../../assets/White-sidebar-icon/Recurring-payment.svg";
import React, { useEffect, useState } from "react";
import AxiosInstance from "../../utils/axiosInstance";
import { Table } from "reactstrap";
import { Close, CheckCircle, Cancel } from "@mui/icons-material";
import styled from "styled-components";
import { LoaderComponent } from "../Icon/Index";
import { NoDataFound } from "../ContractComponent/Index";
import { useUserType } from "../../contexts/UserTypeContext";
import { handleAuth } from "../Login/Auth";
import { Navigate, useLocation } from "react-router-dom";
import { useUserTimezoneFormatter } from "../UserTimezoneDateFormatter/UserTimezoneDateFormatter";
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "8px",
    boxShadow: "var(--shadow-lg)",
    maxWidth: "1000px",
    width: "100%",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme, isDonationMode }) => ({
  fontWeight: 600,
  padding: "14px 16px",
  fontSize: "13px",
  color: "#0f2b46",
  backgroundColor: "#f8fafc",
  borderBottom: `1px solid #e2e8f0`,
  whiteSpace: "nowrap",
  "&:first-of-type": {
    paddingLeft: "20px",
  },
  "&:last-child": {
    paddingRight: "20px",
  },
}));

const BODY_CELL_WIDTHS = {
  date: "18%",
  amount: "10%",
  method: "8%",
  transactionId: "24%",
  status: "12%",
  reason: "28%",
};

const StyledTableRow = styled(TableRow)(({ theme, isDonationMode }) => ({
  "& td": {
    padding: "14px 16px",
    fontSize: "14px",
    color: "#334155",
    borderBottom: `1px solid #e2e8f0`,
    verticalAlign: "middle",
    "&:first-of-type": {
      paddingLeft: "20px",
    },
    "&:last-child": {
      paddingRight: "20px",
    },
  },
}));

const ReasonCell = styled(TableCell)(({ theme }) => ({
  wordWrap: "break-word",
  wordBreak: "break-word",
  whiteSpace: "normal",
  overflowWrap: "break-word",
}));

const StatusChip = styled(Chip)(({ status, isDonationMode }) => ({
  backgroundColor:
    status === "Success" ? "rgba(8, 159, 87, 0.1)" : "rgba(212, 148, 58, 0.1)",
  color:
    status === "Success" ? "#089F57" : isDonationMode ? "#68b2cb" : "var(--amber)",
  fontWeight: 600,
  padding: "4px 10px",
  borderRadius: "6px",
  fontSize: "12px",
  height: "26px",
  "& .MuiChip-icon": {
    color:
      status === "Success" ? "#089F57" : isDonationMode ? "#68b2cb" : "var(--amber)",
    fontSize: "16px",
  },
}));

const DialogComponent = ({
  addStatusOpen,
  handleDialogClose,
  setStatusData,
  statusData,
  recurringId,
  isDonationMode: isDonationModeProp,
}) => {
  const [loader, setLoader] = useState(false);
  const theme = useTheme();
  const { userType } = useUserType();
  const isDonationMode = isDonationModeProp !== undefined ? isDonationModeProp : (userType === "donor");
  const [dateFormat, setDateFormat] = useState("MM/DD/YYYY");
  const [timeFormat, setTimeFormat] = useState("12h");
  const location = useLocation();
  
  // Use timezone-aware date formatter hook
  const formatDateWithUserTimezone = useUserTimezoneFormatter();
  
  const getDatas = async () => {
    try {
      setLoader(true);
      setStatusData(null); // Reset data before fetching
      const res = await AxiosInstance.get(
        `/v1/recurring-payment/recurring-status/${recurringId}`
      );
      if (res?.data?.statusCode === 200 && res?.data?.data) {
        setStatusData(res.data.data);
      } else {
        // If no data or different status code, set empty array
        setStatusData([]);
      }
    } catch (error) {
      console.error("Error fetching status data:", error);
      // Set empty array on error so UI shows "No Data Found"
      setStatusData([]);
    } finally {
      setLoader(false);
    }
  };
  const fetchDatas = async () => {
    try {
      const res = await handleAuth(Navigate, location);
      
      setDateFormat(res?.themes?.Format);
      // Get time format setting
      const timeFormatSetting = res?.data?.TimeFormat || "12h";
      setTimeFormat(timeFormatSetting);
    } catch (error) {
      console.error("Error fetching data:", error);
      setTimeFormat("12h");
    }
  };
  useEffect(() => {
    fetchDatas();
  }, []);
  useEffect(() => {
    if (addStatusOpen && recurringId) {
      getDatas();
    }
  }, [addStatusOpen, recurringId]);

  return (
    <StyledDialog
      open={addStatusOpen}
      onClose={handleDialogClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle
        sx={{
          backgroundColor: "#ffffff",
          color: "#0f2b46",
          padding: "18px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `2px solid ${isDonationMode ? "#68b2cb" : "var(--amber)"}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <img 
            src={RecurringPledges} 
            alt="" 
            style={{ 
              width: "24px", 
              height: "24px",
              filter: "brightness(0) saturate(100%)",
            }} 
          />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "18px", color: "#0f2b46" }}>
            {isDonationMode ? "Recurring Donations Logs" : "Recurring Payments Logs"}
          </Typography>
        </Box>
        <IconButton
          onClick={handleDialogClose}
          size="small"
          sx={{
            color: "#0f2b46",
            "&:hover": {
              backgroundColor: "rgba(6, 49, 100, 0.1)",
            },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loader ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <LoaderComponent
              loader={loader}
              height="50"
              width="50"
              isDonationMode={isDonationMode}
            />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              maxHeight: "550px",
              backgroundColor: "#ffffff",
              "&::-webkit-scrollbar": {
                width: "6px",
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f5f9",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#cbd5e1",
                borderRadius: "3px",
                "&:hover": {
                  background: "#94a3b8",
                },
              },
            }}
          >
            <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell isDonationMode={isDonationMode} align="left" sx={{ width: BODY_CELL_WIDTHS.date }}>
                    Date
                  </StyledTableCell>
                  <StyledTableCell isDonationMode={isDonationMode} align="right" sx={{ width: BODY_CELL_WIDTHS.amount }}>
                    Amount
                  </StyledTableCell>
                  <StyledTableCell isDonationMode={isDonationMode} align="left" sx={{ width: BODY_CELL_WIDTHS.method }}>
                    Method
                  </StyledTableCell>
                  <StyledTableCell isDonationMode={isDonationMode} align="left" sx={{ width: BODY_CELL_WIDTHS.transactionId }}>
                    Transaction ID
                  </StyledTableCell>
                  <StyledTableCell isDonationMode={isDonationMode} align="center" sx={{ width: BODY_CELL_WIDTHS.status }}>
                    Status
                  </StyledTableCell>
                  <StyledTableCell isDonationMode={isDonationMode} align="left" sx={{ width: BODY_CELL_WIDTHS.reason }}>
                    Reason
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statusData && statusData.length > 0 ? (
                  statusData.map((item, index) => (
                    <StyledTableRow key={index} isDonationMode={isDonationMode}>
                      <TableCell align="left" sx={{ width: BODY_CELL_WIDTHS.date }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "13px" }}>
                          {item.createdAt ? formatDateWithUserTimezone(item?.createdAt, {
                            dateFormat: dateFormat,
                            timeFormat: timeFormat,
                            includeSeconds: false
                          }) : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ width: BODY_CELL_WIDTHS.amount }}>
                        <Typography
                          variant="body2"
                          className={`${
                            isDonationMode
                              ? "text-donor-color"
                              : "text-blue-color"
                          }`}
                          sx={{ fontWeight: 600, fontSize: "14px" }}
                        >
                          {item?.amount || "$0.00"}
                        </Typography>
                      </TableCell>
                      <TableCell align="left" sx={{ width: BODY_CELL_WIDTHS.method }}>
                        <Typography variant="body2" sx={{ fontSize: "13px", textTransform: "capitalize" }}>
                          {item.method || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          width: BODY_CELL_WIDTHS.transactionId,
                          maxWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={item.transactionid || ""}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "12px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.transactionid || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ width: BODY_CELL_WIDTHS.status }}>
                        <StatusChip
                          icon={
                            item?.IsPaymentSuccess ? (
                              <CheckCircle />
                            ) : (
                              <Cancel />
                            )
                          }
                          label={item?.IsPaymentSuccess ? "Success" : "Failed"}
                          status={item?.IsPaymentSuccess ? "Success" : "Failed"}
                          isDonationMode={isDonationMode}
                        />
                      </TableCell>
                      <ReasonCell align="left" sx={{ width: BODY_CELL_WIDTHS.reason }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: item?.IsPaymentSuccess
                              ? "#089F57"
                              : isDonationMode
                              ? "#68b2cb"
                              : "var(--amber)",
                            fontWeight: 500,
                            fontSize: "13px",
                            lineHeight: 1.5,
                            wordWrap: "break-word",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {item?.responsetext || "N/A"}
                        </Typography>
                      </ReasonCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <NoDataFound isDonationMode={isDonationMode} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </StyledDialog>
  );
};

export default DialogComponent;
